import { motion, AnimatePresence } from 'framer-motion';
import TableCenterTimer from 'components/desktop/TableCenterTimer';
import { potZoneCoord, handCountdownZoneCoord } from 'constants/seatLayout';
import type { SeatLayoutProfile } from 'constants/seatLayout';
import { OctroPotCoinGraphic } from 'components/OctroGoldCoin';
import { formatGroupedAmount } from 'lib/currency-format';
import { useCurrency } from 'hooks/useCurrency';

interface TableCenterHubProps {
  potAmount: number;
  bootAmount?: number;
  isDealing?: boolean;
  phase?: string;
  potBump?: number;
  lastBetAmount?: number;
  handCountdown?: number | null;
  handCountdownMax?: number;
  handCountdownLabel?: string;
  winSequenceActive?: boolean;
  winPhase?: 'idle' | 'showdown' | 'collect' | 'crown';
  potCollecting?: boolean;
  compact?: boolean;
  layoutProfile?: SeatLayoutProfile;
  /** Hide boot/pot panel — timer only (shar vote, hand start) */
  timerOnly?: boolean;
}

/** Portrait compact hub — scale panel padding/type from displayed amounts */
function potPanelScaleClass(
  potAmount: number,
  bootAmount: number,
  showPotAmount: boolean,
  compact: boolean
): string {
  if (!compact) return '';
  const bootDigits = String(Math.max(0, Math.floor(bootAmount))).length;
  const potDigits =
    showPotAmount && potAmount > 0 ? String(Math.floor(potAmount)).length : 0;
  const digits = Math.max(bootDigits, potDigits);
  if (digits <= 2) return 'tp-pot-hub-panel-scale-xs';
  if (digits === 3) return 'tp-pot-hub-panel-scale-sm';
  if (digits === 4) return 'tp-pot-hub-panel-scale-md';
  return 'tp-pot-hub-panel-scale-lg';
}

export default function TableCenterHub({
  potAmount,
  bootAmount = 10,
  isDealing,
  phase,
  potBump = 0,
  lastBetAmount: _lastBetAmount,
  handCountdown,
  handCountdownMax = 10,
  handCountdownLabel,
  winSequenceActive: _winSequenceActive = false,
  winPhase = 'idle',
  potCollecting = false,
  compact = false,
  layoutProfile = 'desktop',
  timerOnly = false,
}: TableCenterHubProps) {
  const { code: currencyCode } = useCurrency();
  const showHandTimer = handCountdown != null && handCountdown > 0;
  const hubProfile =
    compact && layoutProfile === 'portrait'
      ? 'portrait'
      : compact
        ? 'mobile'
        : layoutProfile;
  const isLandscapeHub = !compact && layoutProfile === 'landscape';
  const isPortraitCompact = compact && layoutProfile === 'portrait';
  const compactCenterTimer = compact || layoutProfile === 'landscape';
  const keepPotVisibleForWin =
    winPhase === 'showdown' || winPhase === 'collect';
  const potAnchor = showHandTimer ? handCountdownZoneCoord(hubProfile) : potZoneCoord(hubProfile);
  const hideBetweenHands =
    !keepPotVisibleForWin &&
    (phase === 'waiting' || phase === 'completed');
  const dealing = Boolean(isDealing) && !hideBetweenHands;
  const showBootPotPanel =
    !hideBetweenHands && !showHandTimer && !timerOnly && winPhase !== 'crown';
  const showPotAmount = showBootPotPanel && !showHandTimer && potAmount > 0;
  const showPotChips = showPotAmount && !potCollecting;
  const panelScaleClass = potPanelScaleClass(
    potAmount,
    bootAmount,
    showPotAmount,
    compact
  );

  return (
    <div
      className={`pointer-events-none absolute z-[26] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center tp-pot-hub-anchor${compact ? ' tp-pot-hub-anchor-mobile' : ''}${isLandscapeHub ? ' tp-pot-hub-anchor-landscape' : ''}${timerOnly && showHandTimer ? ' tp-pot-hub-anchor-timer-only' : ''}`}
      style={{ left: `${potAnchor.x}%`, top: `${potAnchor.y}%` }}
    >
      <div className={`tp-pot-hub w-max max-w-none${compact ? ' tp-pot-hub-compact' : ''}${isPortraitCompact ? ' tp-pot-hub-portrait' : ''}${isLandscapeHub ? ' tp-pot-hub-landscape' : ''}${timerOnly && showHandTimer ? ' tp-pot-hub-timer-only' : ''}`}>
        {showHandTimer && (
          <TableCenterTimer
            seconds={handCountdown!}
            maxSeconds={handCountdownMax}
            label={handCountdownLabel ?? 'Next hand'}
            variant="countdown"
            compact={compactCenterTimer}
            className={
              compactCenterTimer
                ? `tp-center-hand-countdown-mobile${layoutProfile === 'landscape' ? ' tp-center-hand-countdown-landscape' : ''}`
                : 'tp-center-hand-countdown-desktop'
            }
          />
        )}

        {showBootPotPanel && (
          <AnimatePresence mode="wait">
            <motion.div
              key={`pot-panel-${potAmount}-${phase}-${winPhase}-${showHandTimer ? 'cd' : 'live'}`}
              className={`tp-pot-hub-panel ${dealing ? 'tp-pot-hub-panel-dealing' : ''} ${showHandTimer ? 'tp-pot-hub-panel-compact' : ''} ${compact ? 'tp-pot-hub-panel-mobile' : ''} ${isPortraitCompact ? 'tp-pot-hub-panel-portrait' : ''} ${panelScaleClass}`}
              initial={{ opacity: 0.88, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22 }}
            >
              <div className="tp-pot-hub-panel-inner">
                <div className="tp-octro-pot" aria-label="Pot">
                  <OctroPotCoinGraphic />
                  <motion.span
                    key={`pot-amt-${potAmount}-${potBump}-${winPhase}`}
                    className={`tp-octro-pot-amount ${potBump > 0 ? 'tp-pot-hub-pot-bump' : ''}`}
                    initial={{ scale: 1 }}
                    animate={{ scale: potBump > 0 ? [1, 1.05, 1] : 1 }}
                    transition={{ duration: 0.55, ease: 'easeOut' }}
                  >
                    {formatGroupedAmount(
                      showPotAmount ? potAmount : bootAmount,
                      currencyCode ?? 'INR'
                    )}
                  </motion.span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        )}

        {showPotChips ? <div className="tp-pot-hub-shadow" aria-hidden /> : null}
      </div>
    </div>
  );
}
